import * as THREE from "three/webgpu";
import {
  PI,
  abs,
  clamp,
  float,
  max,
  mix,
  positionLocal,
  sin,
  smoothstep,
  step,
  uniform,
  vec3,
} from "three/tsl";

const TERRAIN_SEGMENTS_X = 64;
const TERRAIN_SEGMENTS_Z = 48;

/** Vertex-shader deformation shared by flood, rift and tornado interactions. */
export class GpuBattleTerrain {
  readonly #mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshToonNodeMaterial>;
  readonly #time = uniform(0);
  readonly #frontline = uniform(0);
  readonly #motionScale = uniform(1);
  readonly #floodStart = uniform(-10);
  readonly #floodSign = uniform(1);
  readonly #riftStart = uniform(-10);
  readonly #riftSign = uniform(1);
  readonly #tornadoStart = uniform(-10);
  readonly #tornadoSign = uniform(1);

  constructor(scene: THREE.Scene) {
    const geometry = new THREE.PlaneGeometry(9.6, 7.4, TERRAIN_SEGMENTS_X, TERRAIN_SEGMENTS_Z);
    geometry.rotateX(-Math.PI / 2);
    const material = new THREE.MeshToonNodeMaterial();
    material.name = "gpu-deformed-dream-rug-material";
    material.transparent = true;
    material.depthWrite = false;
    material.side = THREE.DoubleSide;

    const floodAge = this.#time.sub(this.#floodStart);
    const floodGate = step(0, floodAge).mul(step(floodAge, 3.6));
    const floodProgress = clamp(floodAge.div(3.6), 0, 1);
    const floodTravel = smoothstep(0.02, 0.92, floodProgress);
    const floodX = this.#frontline.add(this.#floodSign.mul(float(0.25).add(floodTravel.mul(4.25))));
    const floodBand = smoothstep(1.25, 0.04, abs(positionLocal.x.sub(floodX)));
    const floodWave = sin(positionLocal.z.mul(4.8).add(this.#time.mul(10.5))).mul(0.11)
      .add(sin(positionLocal.z.mul(10.2).sub(this.#time.mul(15))).mul(0.045));
    const floodHeight = max(0, floodBand.mul(float(0.34).add(floodWave)))
      .mul(floodGate)
      .mul(sin(floodProgress.mul(PI)))
      .mul(this.#motionScale);

    const riftAge = this.#time.sub(this.#riftStart);
    const riftGate = step(0, riftAge).mul(step(riftAge, 4));
    const riftProgress = clamp(riftAge.div(4), 0, 1);
    const riftX = this.#frontline.add(this.#riftSign.mul(2.05));
    const riftDistance = abs(positionLocal.x.sub(riftX));
    const riftRidge = smoothstep(2.75, 0.08, riftDistance);
    const fracture = abs(sin(positionLocal.x.mul(7.5).add(positionLocal.z.mul(3.7))))
      .mul(0.46)
      .add(sin(positionLocal.z.mul(9).sub(this.#time.mul(5))).mul(0.08));
    const riftHeight = max(0, fracture)
      .mul(riftRidge)
      .mul(riftGate)
      .mul(sin(riftProgress.mul(PI)))
      .mul(this.#motionScale);

    const tornadoAge = this.#time.sub(this.#tornadoStart);
    const tornadoGate = step(0, tornadoAge).mul(step(tornadoAge, 4.2));
    const tornadoProgress = clamp(tornadoAge.div(4.2), 0, 1);
    const tornadoX = this.#frontline.add(this.#tornadoSign.mul(1.55));
    const dx = positionLocal.x.sub(tornadoX);
    const radius = dx.mul(dx).add(positionLocal.z.mul(positionLocal.z)).sqrt();
    const movingRing = abs(radius.sub(tornadoProgress.mul(3.5)));
    const tornadoRing = smoothstep(0.7, 0.02, movingRing);
    const tornadoHeight = tornadoRing.mul(sin(radius.mul(8).sub(this.#time.mul(13))))
      .mul(0.24)
      .mul(tornadoGate)
      .mul(sin(tornadoProgress.mul(PI)))
      .mul(this.#motionScale);

    const totalHeight = floodHeight.add(riftHeight).add(tornadoHeight);
    material.positionNode = vec3(positionLocal.x, positionLocal.y.add(totalHeight), positionLocal.z);

    const floodStrength = clamp(floodHeight.mul(2.6), 0, 1);
    const riftStrength = clamp(riftHeight.mul(2.2), 0, 1);
    const stormStrength = clamp(abs(tornadoHeight).mul(3.6), 0, 1);
    const baseColor = vec3(0.46, 0.55, 0.5);
    const floodColor = mix(baseColor, vec3(0.08, 0.72, 0.92), floodStrength);
    const riftColor = mix(floodColor, vec3(1, 0.55, 0.12), riftStrength);
    const surfaceColor = mix(riftColor, vec3(0.7, 0.85, 1), stormStrength);
    material.colorNode = surfaceColor.mul(1.35);
    material.opacityNode = clamp(floodStrength.add(riftStrength).add(stormStrength).mul(0.72), 0, 0.76);

    this.#mesh = new THREE.Mesh(geometry, material);
    this.#mesh.name = "gpu-deformed-dream-rug";
    this.#mesh.position.y = 0.035;
    this.#mesh.frustumCulled = false;
    this.#mesh.renderOrder = 4;
    scene.add(this.#mesh);
  }

  triggerFlood(sign: number, time: number): void {
    this.#floodSign.value = Math.sign(sign) || 1;
    this.#floodStart.value = time;
  }

  triggerRift(sign: number, time: number): void {
    this.#riftSign.value = Math.sign(sign) || 1;
    this.#riftStart.value = time;
  }

  triggerTornado(sign: number, time: number): void {
    this.#tornadoSign.value = Math.sign(sign) || 1;
    this.#tornadoStart.value = time;
  }

  update(time: number, frontline: number, reducedMotion: boolean): void {
    this.#time.value = time;
    this.#frontline.value = frontline;
    this.#motionScale.value = reducedMotion ? 0.28 : 1;
  }

  reset(): void {
    this.#floodStart.value = -10;
    this.#riftStart.value = -10;
    this.#tornadoStart.value = -10;
  }

  snapshot(): { vertexSimulation: true; vertices: number } {
    return { vertexSimulation: true, vertices: (TERRAIN_SEGMENTS_X + 1) * (TERRAIN_SEGMENTS_Z + 1) };
  }

  dispose(): void {
    this.#mesh.removeFromParent();
    this.#mesh.geometry.dispose();
    this.#mesh.material.dispose();
  }
}
